import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CaretDown, CaretUp, Calendar } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsByIP = () => {
  const navigate = useNavigate();
  const [ipGroups, setIpGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIPs, setExpandedIPs] = useState({});
  const [activeTab, setActiveTab] = useState('today'); // 'today' or 'all'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchDuplicateIPs();
  }, [activeTab, selectedDate]);

  const fetchDuplicateIPs = async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/clients/duplicate-ips`;
      
      // Add date parameter based on active tab
      if (activeTab === 'today') {
        url += `?date=${new Date().toISOString().split('T')[0]}`;
      } else if (activeTab === 'all' && selectedDate) {
        url += `?date=${selectedDate}`;
      }
      
      const response = await axios.get(url);
      const groups = (Array.isArray(response.data) ? response.data : []).map((group) => {
        const clients = Array.isArray(group.clients) ? group.clients : [];
        const duplicateUsers = Array.isArray(group.duplicate_users) ? group.duplicate_users : clients.slice(1);
        return {
          ...group,
          original_user: group.original_user || clients[0] || null,
          duplicate_users: duplicateUsers,
          duplicate_count: group.duplicate_count ?? duplicateUsers.length
        };
      }).filter((group) => group.original_user);
      setIpGroups(groups);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch duplicate IPs:', error);
      toast.error('Failed to load duplicate IPs');
      setLoading(false);
    }
  };

  const toggleExpand = (ip) => {
    setExpandedIPs(prev => ({
      ...prev,
      [ip]: !prev[ip]
    }));
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-screen flex flex-col bg-wa-hover">
      {/* Header */}
      <div className="bg-wa-primary-strong text-white p-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin-settings')}
            className="p-2 hover:bg-wa-primary-strong rounded-full"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Clients with Same IP Address</h1>
            <p className="text-sm text-green-100">Track duplicate registrations by IP</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto">
          {/* Tabs */}
          <div className="bg-wa-panel rounded-lg shadow-sm mb-4 p-1 flex gap-2">
            <button
              onClick={() => setActiveTab('today')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
                activeTab === 'today'
                  ? 'bg-wa-primary-strong text-white'
                  : 'bg-wa-hover text-wa-text-primary hover:bg-wa-hover'
              }`}
            >
              📅 Today
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
                activeTab === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-wa-hover text-wa-text-primary hover:bg-wa-hover'
              }`}
            >
              📊 All
            </button>
          </div>

          {/* Date Picker (Only in All tab) */}
          {activeTab === 'all' && (
            <div className="bg-wa-panel rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-center gap-3">
                <Calendar size={24} className="text-wa-text-secondary" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-wa-text-primary mb-1">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-wa-text-secondary">Loading...</div>
            </div>
          ) : ipGroups.length === 0 ? (
            <div className="text-center py-12 text-wa-text-secondary">
              <p className="text-lg">✅ No duplicate IP addresses found</p>
              <p className="text-sm mt-2">All clients have unique IPs</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ipGroups.map((group) => (
                <div
                  key={group.ip_address}
                  className="bg-wa-panel rounded-lg shadow-md border-l-4 border-red-500 overflow-hidden"
                >
                  {/* Original User (Permanent) */}
                  <div className="p-4 border-b bg-wa-hover">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {/* Profile Picture */}
                        <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                          {group.original_user.profile_pic ? (
                            <img
                              src={group.original_user.profile_pic.startsWith('http') ? group.original_user.profile_pic : `${API}${group.original_user.profile_pic}`}
                              alt={group.original_user.username}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            group.original_user.username?.charAt(0)?.toUpperCase() || 'U'
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-wa-text-primary">
                              {group.original_user.username}
                            </h3>
                            <span className="bg-wa-hover text-wa-text-primary text-xs px-2 py-0.5 rounded-full font-medium">
                              ✅ Original
                            </span>
                          </div>
                          <p className="text-sm text-wa-text-secondary">{group.original_user.phone}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-xs text-wa-text-secondary">
                              🌐 IP: {group.ip_address}
                            </p>
                            <p className="text-xs text-wa-text-secondary">
                              📅 {formatDate(group.original_user.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Duplicate Count Badge & Expand Button */}
                      <div className="flex items-center gap-3">
                        <div className="bg-red-500 text-white rounded-full px-4 py-2 font-bold text-lg">
                          {group.duplicate_count}
                        </div>
                        <button
                          onClick={() => toggleExpand(group.ip_address)}
                          className="p-2 bg-orange-200 hover:bg-orange-300 rounded-lg transition-colors"
                        >
                          {expandedIPs[group.ip_address] ? (
                            <CaretUp size={24} weight="bold" className="text-wa-text-primary" />
                          ) : (
                            <CaretDown size={24} weight="bold" className="text-wa-text-primary" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Duplicate Users (Expandable) */}
                  {expandedIPs[group.ip_address] && (
                    <div className="p-4 bg-wa-hover border-t">
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-wa-text-primary">
                          ⚠️ Same IP Users ({group.duplicate_count})
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {group.duplicate_users.map((user, idx) => (
                          <div
                            key={user.id}
                            className="flex items-center gap-3 p-3 bg-wa-panel rounded-lg border border-orange-200"
                          >
                            <div className="flex items-center justify-center bg-orange-600 text-white font-bold w-8 h-8 rounded-full text-sm">
                              {idx + 1}
                            </div>
                            
                            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold">
                              {user.profile_pic ? (
                                <img
                                  src={user.profile_pic.startsWith('http') ? user.profile_pic : `${API}${user.profile_pic}`}
                                  alt={user.username}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                user.username?.charAt(0)?.toUpperCase() || 'U'
                              )}
                            </div>

                            <div className="flex-1">
                              <div className="font-semibold text-wa-text-primary">{user.username}</div>
                              <div className="text-sm text-wa-text-secondary">{user.phone}</div>
                            </div>

                            <div className="text-right">
                              <div className="text-xs text-wa-text-secondary">
                                {formatDate(user.created_at)}
                              </div>
                              <span className="text-xs bg-wa-hover text-wa-text-primary px-2 py-0.5 rounded-full mt-1 inline-block">
                                ⚠️ Same IP
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientsByIP;
